import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Lock, X, CheckCircle2, AlertCircle } from 'lucide-react';
import baseurl from '../Baseurl/baseurl';
import { AuthLayout, FormField, Input, Button } from '../ui';

const ResetPasswordPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Get email from navigation state
  const email = location.state?.email;

  useEffect(() => {
    // Redirect to forgot password if no email
    if (!email) {
      navigate('/forgot-password');
    }
  }, [email, navigate]);

  const handleResetPassword = async () => {
    setError('');

    if (!newPassword || !confirmPassword) {
      setError('Both password fields are required');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setIsLoading(true);
      const response = await fetch(`${baseurl}/api/member/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          newPassword: newPassword
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.msg || 'Failed to reset password');
      }

      setSnackbar({
        open: true,
        message: 'Password reset successfully!',
        severity: 'success',
      });

      // Redirect to login page after successful reset
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (err) {
      setError(err.message);
      setSnackbar({
        open: true,
        message: err.message,
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!email) {
    return null; // Will redirect in useEffect
  }

  return (
    <AuthLayout
      eyebrow="KBC · Account recovery"
      title={<>Reset your <em className="kbc-serif">password</em>.</>}
      subtitle="Create a new password for your account to regain secure access to the community directory."
      footer={
        <>
          Remember your password?{' '}
          <Link to="/" className="font-semibold text-primary hover:text-primary-hi">
            Back to Sign In
          </Link>
        </>
      }
    >
      <div className="space-y-5">
        <FormField label="New Password" htmlFor="new-password" required>
          <Input
            id="new-password"
            type={showNewPassword ? 'text' : 'password'}
            placeholder="Enter new password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            leftIcon={<Lock className="h-[18px] w-[18px]" />}
            rightIcon={
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="text-muted transition-colors hover:text-ink"
                aria-label={showNewPassword ? 'Hide password' : 'Show password'}
              >
                {showNewPassword ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
              </button>
            }
          />
        </FormField>

        <FormField
          label="Confirm Password"
          htmlFor="confirm-password"
          required
          hint="Password must be at least 8 characters long"
        >
          <Input
            id="confirm-password"
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            leftIcon={<Lock className="h-[18px] w-[18px]" />}
            rightIcon={
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="text-muted transition-colors hover:text-ink"
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                {showConfirmPassword ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
              </button>
            }
          />
        </FormField>

        {error && (
          <div className="flex items-center gap-2 rounded-md border border-danger/20 bg-danger/10 px-4 py-3 text-sm font-medium text-danger">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {error}
          </div>
        )}

        <Button onClick={handleResetPassword} loading={isLoading} fullWidth size="lg">
          {isLoading ? 'Resetting Password…' : 'Reset Password'}
        </Button>
      </div>

      {/* Snackbar */}
      {snackbar.open && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 animate-slideUp">
          <div
            className={`flex items-center gap-3 rounded-full px-5 py-3 text-sm font-medium text-white shadow-lift ${
              snackbar.severity === 'success' ? 'bg-success' : 'bg-danger'
            }`}
          >
            {snackbar.severity === 'success' ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
            <span>{snackbar.message}</span>
            <button onClick={() => setSnackbar({ ...snackbar, open: false })} className="opacity-80 hover:opacity-100">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </AuthLayout>
  );
};

export default ResetPasswordPage;
